import { useState, forwardRef, useImperativeHandle, useMemo } from "react";
import { Button, Icon, Select } from "@legal-500/legal500-design-system";
import type { DisplayFilterRule, SubmissionFilters } from "@/api/survey-library.service";
import type { DisplayRuleOptions } from "@/api/surveyTaxonomy.service";
import styles from "./QuestionLibrary.module.css";

const REFEREE_BUSINESS_TYPE_OPTIONS = [
  { value: "", label: "— None —" },
  { value: "Instructing solicitor", label: "Instructing solicitor" },
  { value: "In-house counsel", label: "In-house counsel" },
  { value: "Other", label: "Other" },
];

const SUBMISSION_TYPE_OPTIONS = [
  { value: "", label: "— None —" },
  { value: "Firm", label: "Firm" },
  { value: "Lawyer", label: "Lawyer" },
];

const REFEREE_TYPE_OPTIONS = [
  { value: "", label: "— None —" },
  { value: "Firm", label: "Firm" },
  { value: "Lawyer", label: "Lawyer" },
  { value: "Barrister", label: "Barrister" },
  { value: "Mediator", label: "Mediator" },
];

const FILTER_KEY_LABELS: Record<string, string> = {
  refereeBusinessType: "Business Type",
  submissionType: "Submission",
  refereeType: "Referee",
  region: "Region",
  practiceGroup: "Practice Group",
  practiceArea: "Practice Area",
};

type FilterRulesEditorHandle = {
  flushPendingRule: () => DisplayFilterRule[] | null;
};

type FilterRulesEditorProps = {
  rules: DisplayFilterRule[];
  onChange: (rules: DisplayFilterRule[]) => void;
  taxonomyOptions?: DisplayRuleOptions;
};

function getTaxonomyLabel(
  key: string,
  value: string,
  taxonomyOptions?: FilterRulesEditorProps["taxonomyOptions"],
): string {
  if (!taxonomyOptions) return value;

  if (key === "region" || key === "practiceGroup" || key === "practiceArea") {
    return taxonomyOptions[key].find(option => option.value === value)?.label ?? value;
  }

  return value;
}

function formatRule(
  rule: DisplayFilterRule,
  taxonomyOptions?: FilterRulesEditorProps["taxonomyOptions"],
): { label: string; value: string }[] {
  const filters = rule.submissionFilters;
  if (!filters) return [];

  const entries: { label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (value) {
      entries.push({
        label: FILTER_KEY_LABELS[key] ?? key,
        value: getTaxonomyLabel(key, value, taxonomyOptions),
      });
    }
  }
  return entries;
}

function normalizeSubmissionFilters(filters: SubmissionFilters): string {
  return Object.entries(filters)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => [key, String(value)] as const)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
}

const FilterRulesEditor = forwardRef<FilterRulesEditorHandle, FilterRulesEditorProps>(
  function FilterRulesEditor({ rules, onChange, taxonomyOptions }, ref) {
    const [isAdding, setIsAdding] = useState(false);
    const [newRefereeBusinessType, setNewRefereeBusinessType] = useState("");
    const [newSubmissionType, setNewSubmissionType] = useState("");
    const [newRefereeType, setNewRefereeType] = useState("");
    const [newRegion, setNewRegion] = useState("");
    const [newPracticeGroup, setNewPracticeGroup] = useState("");
    const [newPracticeArea, setNewPracticeArea] = useState("");
    const [addRuleError, setAddRuleError] = useState("");

    const handleDeleteRule = (index: number) => {
      onChange(rules.filter((_, i) => i !== index));
    };

    const handleSaveRule = () => {
      const submissionFilters: SubmissionFilters = {};
      if (newRefereeBusinessType) submissionFilters.refereeBusinessType = newRefereeBusinessType;
      if (newSubmissionType) submissionFilters.submissionType = newSubmissionType;
      if (newRefereeType) submissionFilters.refereeType = newRefereeType;
      if (newRegion) submissionFilters.region = newRegion;
      if (newPracticeGroup) submissionFilters.practiceGroup = newPracticeGroup;
      if (newPracticeArea) submissionFilters.practiceArea = newPracticeArea;

      if (Object.keys(submissionFilters).length === 0) return;

      const normalizedNewRule = normalizeSubmissionFilters(submissionFilters);
      const hasDuplicate = rules.some(
        rule => normalizeSubmissionFilters(rule.submissionFilters ?? {}) === normalizedNewRule,
      );

      if (hasDuplicate) {
        setAddRuleError("This display rule combination already exists.");
        return;
      }

      onChange([...rules, { submissionFilters }]);
      resetAddForm();
    };

    const handleCancelAdd = () => {
      resetAddForm();
    };

    const resetAddForm = () => {
      setIsAdding(false);
      setNewRefereeBusinessType("");
      setNewSubmissionType("");
      setNewRefereeType("");
      setNewRegion("");
      setNewPracticeGroup("");
      setNewPracticeArea("");
      setAddRuleError("");
    };

    const flushPendingRule = (): DisplayFilterRule[] | null => {
      if (!isAdding) return null;
      const submissionFilters: SubmissionFilters = {};
      if (newRefereeBusinessType) submissionFilters.refereeBusinessType = newRefereeBusinessType;
      if (newSubmissionType) submissionFilters.submissionType = newSubmissionType;
      if (newRefereeType) submissionFilters.refereeType = newRefereeType;
      if (newRegion) submissionFilters.region = newRegion;
      if (newPracticeGroup) submissionFilters.practiceGroup = newPracticeGroup;
      if (newPracticeArea) submissionFilters.practiceArea = newPracticeArea;
      if (Object.keys(submissionFilters).length === 0) return null;
      const normalizedNew = normalizeSubmissionFilters(submissionFilters);
      const isDuplicate = rules.some(
        rule => normalizeSubmissionFilters(rule.submissionFilters ?? {}) === normalizedNew,
      );
      if (isDuplicate) return null;
      const newRules = [...rules, { submissionFilters }];
      onChange(newRules);
      resetAddForm();
      return newRules;
    };

    useImperativeHandle(ref, () => ({ flushPendingRule }));

    const hasAnyFilter = Boolean(
      newRefereeBusinessType ||
        newSubmissionType ||
        newRefereeType ||
        newRegion ||
        newPracticeGroup ||
        newPracticeArea,
    );
    const newSubmissionFilters: SubmissionFilters = {};
    if (newRefereeBusinessType) newSubmissionFilters.refereeBusinessType = newRefereeBusinessType;
    if (newSubmissionType) newSubmissionFilters.submissionType = newSubmissionType;
    if (newRefereeType) newSubmissionFilters.refereeType = newRefereeType;
    if (newRegion) newSubmissionFilters.region = newRegion;
    if (newPracticeGroup) newSubmissionFilters.practiceGroup = newPracticeGroup;
    if (newPracticeArea) newSubmissionFilters.practiceArea = newPracticeArea;
    const isDuplicateCandidate =
      hasAnyFilter &&
      rules.some(
        rule =>
          normalizeSubmissionFilters(rule.submissionFilters ?? {}) ===
          normalizeSubmissionFilters(newSubmissionFilters),
      );

    const practiceAreaSelectOptions = useMemo(() => {
      if (!taxonomyOptions) return [];
      if (newRegion === "") return taxonomyOptions.practiceArea;
      return taxonomyOptions.practiceArea
        .filter(opt => opt.region === newRegion)
        .map(opt => ({ ...opt, label: opt.baseName }));
    }, [taxonomyOptions, newRegion]);

    return (
      <div className={styles.filterRulesEditor}>
        <label className={styles.fieldLabel}>Display rules</label>

        {rules.length === 0 && !isAdding && (
          <span className={styles.mutedText}>No display rules — shown to all respondents</span>
        )}

        {rules.length > 0 && (
          <div className={styles.filterRulesList}>
            {rules.map((rule, ruleIdx) => {
              const entries = formatRule(rule, taxonomyOptions);
              if (entries.length === 0) return null;
              return (
                <div key={ruleIdx} className={styles.rulePillEditable}>
                  <div className={styles.rulePillContent}>
                    {entries.map((entry, idx) => (
                      <span key={`${entry.label}-${entry.value}`} className={styles.ruleTag}>
                        {idx > 0 && <span className={styles.pillSeparator}>And</span>}
                        <span className={styles.ruleTagLabel}>{entry.label}:</span>
                        <span className={styles.ruleTagValue}>{entry.value}</span>
                      </span>
                    ))}
                  </div>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    className={styles.ruleDeleteButton}
                    aria-label="Remove display rule"
                    onClick={() => handleDeleteRule(ruleIdx)}
                  >
                    <Icon icon="delete" size="large" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {isAdding && (
          <div className={styles.addRuleForm}>
            <div className={styles.addRuleSelects}>
              <Select
                label="Referee Business Type"
                options={REFEREE_BUSINESS_TYPE_OPTIONS}
                value={newRefereeBusinessType}
                onChange={e => {
                  setNewRefereeBusinessType(e.target.value);
                  setAddRuleError("");
                }}
                fullWidth
              />
              <Select
                label="Submission Type"
                options={SUBMISSION_TYPE_OPTIONS}
                value={newSubmissionType}
                onChange={e => {
                  setNewSubmissionType(e.target.value);
                  setAddRuleError("");
                }}
                fullWidth
              />
              <Select
                label="Referee Type"
                options={REFEREE_TYPE_OPTIONS}
                value={newRefereeType}
                onChange={e => {
                  setNewRefereeType(e.target.value);
                  setAddRuleError("");
                }}
                fullWidth
              />
              {taxonomyOptions && (
                <>
                  <Select
                    label="Region"
                    options={[{ value: "", label: "— None —" }, ...taxonomyOptions.region]}
                    value={newRegion}
                    onChange={e => {
                      setNewRegion(e.target.value);
                      setAddRuleError("");
                    }}
                    fullWidth
                  />
                  <Select
                    label="Practice Group"
                    options={[{ value: "", label: "— None —" }, ...taxonomyOptions.practiceGroup]}
                    value={newPracticeGroup}
                    onChange={e => {
                      setNewPracticeGroup(e.target.value);
                      setAddRuleError("");
                    }}
                    fullWidth
                  />
                  <Select
                    label="Practice Area"
                    options={[{ value: "", label: "— None —" }, ...practiceAreaSelectOptions]}
                    value={newPracticeArea}
                    onChange={e => {
                      setNewPracticeArea(e.target.value);
                      setAddRuleError("");
                    }}
                    fullWidth
                  />
                </>
              )}
            </div>
            {(addRuleError || isDuplicateCandidate) && (
              <span className={styles.fieldError}>
                {addRuleError || "This display rule combination already exists."}
              </span>
            )}
            <div className={styles.addRuleActions}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSaveRule}
                disabled={!hasAnyFilter || isDuplicateCandidate}
              >
                Save Rule
              </Button>
              <Button variant="outlined" size="small" onClick={handleCancelAdd}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!isAdding && (
          <div className={styles.addRuleButtonRow}>
            <Button variant="outlined" size="small" onClick={() => setIsAdding(true)}>
              + Add Display Rule
            </Button>
          </div>
        )}
      </div>
    );
  },
);

export { FilterRulesEditor };
export type { FilterRulesEditorHandle };
